import Accordion from '@/common/components/Accordion'
import Select from '@/common/components/Select'
import useDebounce from '@/common/hooks/useDebounce'
import { Option } from '@/common/types/option'
import { BranchConsumptionRestriction } from '@/features/privateCompanies/components/createBranchForm/consumptionRestriction/branchConsumptionRestriction'
import { ProductCategoryConsumptionRestriction } from '@/features/privateCompanies/components/createBranchForm/consumptionRestriction/productCategoryConsumptionRestriction'
import { useClientCompaniesAvailableToAllocateInBranchesSummary } from '@/features/privateCompanies/hooks/useClientCompaniesAvailableToAllocateInBranchesSummary'
import { CompanyBranchValidationSchema } from '@/features/privateCompanies/schemas/createBranch.schema'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/privateCompanies/services/allowedCategoriesAndProductsTransformer'
import { CircleAlert } from 'lucide-react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ConsumptionRestrictionProps {
  disabled?: boolean
}

export const ConsumptionRestriction = ({ disabled }: ConsumptionRestrictionProps) => {
  const { companyId } = useParams()

  const [searchProductCategory, setSearchProductCategory] = useDebounce('')

  const { data: allowedProductCategories } = useClientCompaniesAvailableToAllocateInBranchesSummary({
    companyId,
    filters: { search: searchProductCategory },
    mapper: ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
      key: 'categories',
      showAvailableTotal: false,
    }),
  })

  const { control } = useFormContext<CompanyBranchValidationSchema>()

  const {
    fields: productCategoryRestrictionsFields,
    append: appendProductCategoryRestrictions,
    remove: removeProductCategoryRestrictions,
  } = useFieldArray({
    control,
    name: 'product_category_restrictions_attributes',
  })

  const handleAppendProductCategory = (option: Option) => {
    if (option.value) {
      appendProductCategoryRestrictions({
        product_category_id: option.value,
        display_name: option.label,
      })
    }
  }

  return (
    <Accordion
      name="consumption_restriction"
      head={<p className="text-sm">Restrição de consumo</p>}
      body={
        <div className="flex flex-col gap-4">
          <BranchConsumptionRestriction disabled={disabled} />

          <p className="text-sm">Restrição de consumo por serviço</p>

          <div className="flex items-center gap-x-2">
            <CircleAlert strokeWidth={1} className="text-warning" />

            <div>
              <p className="text-xs">
                Ao definir restrições para serviços específicos, estas configurações terão prioridade sobre as
                restrições de consumo acima
              </p>
            </div>
          </div>

          <Select
            name="search_product_categories"
            placeholder="Pesquisar serviços"
            value=""
            options={
              allowedProductCategories?.filter((productCategory) => {
                return !productCategoryRestrictionsFields?.some(
                  ({ product_category_id }) => product_category_id === productCategory.value,
                )
              }) || []
            }
            onChange={(option) => handleAppendProductCategory(option as Option)}
            onInputChange={setSearchProductCategory}
            isDisabled={disabled}
          />

          {productCategoryRestrictionsFields.map((category, index) => (
            <ProductCategoryConsumptionRestriction
              key={category.id}
              category={category}
              categoryIndex={index}
              removeCategory={() => removeProductCategoryRestrictions(index)}
              disabled={disabled}
            />
          ))}
        </div>
      }
    />
  )
}
